// absolute
import { Autocomplete, Box, InputAdornment, TextField } from '@mui/material';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// relative imports
import { checkInvalidInput } from '../../source-management/manual-entry/utilities/manualUtilities';
import { VirtualizedListboxComponent } from '../custom-components/AutoCompleteTextField';
import PopperVirtualized from '../custom-components/LedgiblePopper';
import Loader from '../custom-components/Loader';

const useVirtualizedStyles = makeStyles()(() => ({
  listbox: {
    width: '100%',
    margin: 0,
    padding: 0,
    zIndex: 1,
    position: 'absolute',
    listStyle: 'none',
    overflow: 'auto',
    maxHeight: 200,
  },
}));

const FormAutoCompleteField = ({
  freeSolo,
  fetching,
  fetchOptions,
  getOptionLabel,
  isOptionEqualToValue,
  handleChange,
  InputProps,
  input: { onChange, onFocus, onBlur, value, ...inputProps },
  limit,
  match,
  meta: { error, touched, pristine, submitFailed, initial },
  noPlaceholderShrink,
  noOptionsText,
  optionList,
  parse,
  TextFieldProps,
  sort,
  sortBy,
  virtualize,
  multiple,
  'data-cy': cypressId,
  ...restAutoCompleteProps
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const { classes: virtualizedClasses } = useVirtualizedStyles();

  const containsError = !!(
    (touched && error && !pristine) ||
    (error && submitFailed)
  );

  const virtualizedProps = virtualize
    ? {
        classes: virtualizedClasses,
        disableListWrap: true,
        ListboxComponent: VirtualizedListboxComponent,
        PopperComponent: (popperProps) => {
          const { modifiers } = popperProps;
          return (
            <PopperVirtualized
              {...popperProps}
              modifiers={[
                ...(modifiers || []),
                { name: 'preventOverflow', enabled: false },
                { name: 'flip', enabled: false },
                { name: 'hide', enabled: false },
              ]}
            />
          );
        },
      }
    : {};

  const getHelperText = () => {
    if (containsError) return error;
    return null;
  };

  const sortedOptions = useMemo(() => {
    const opts = options.length > 0 ? options : optionList || [];
    if (sort) return sortBy ? sortBy([...opts]) : [...opts].sort();
    return opts;
  }, [options, optionList, sort, sortBy]);

  const handleOptionLabel = useCallback((opt) => {
    return getOptionLabel(opt)
  }, [getOptionLabel])

  const getAsyncOptions = useCallback(
    async (query) => {
      if (fetchOptions) {
        const results = await fetchOptions(query);
        return results || [];
      }

      if (!Array.isArray(sortedOptions) || !sortedOptions.length) return [];

      return new Promise((resolve) => {
        setTimeout(() => {
          if (!Array.isArray(sortedOptions) || !sortedOptions.length) {
            return resolve([]);
          }

          if (!query) {
            return resolve(sortedOptions);
          }

          let filteredOptions = (sortedOptions || []).filter((o) => {
            const searchValue =
              typeof o === 'string' ? o : handleOptionLabel(o);
            if (!searchValue) return false;

            if (match === 'start') {
              return searchValue.toLowerCase().startsWith(query.toLowerCase());
            }
            return searchValue.toLowerCase().indexOf(query.toLowerCase()) > -1;
          });

          if (typeof limit === 'number') {
            filteredOptions = filteredOptions.slice(0, limit);
          }
          return resolve(filteredOptions);
        }, 100);
      });
    },
    [fetchOptions, limit, match, sortedOptions, handleOptionLabel],
  );

  const getOptionsDelayed = useMemo(
    () =>
      debounce(async (query, callback) => {
        setLoading(true);
        try {
          const results = await getAsyncOptions(query);
          callback(results);
        } catch (e) {
          console.error('Error fetching options:', e);
          callback([]);
        } finally {
          setLoading(false);
        }
      }, 500),
    [getAsyncOptions],
  );

  useEffect(() => {
    if (
      !inputValue &&
      optionList &&
      Array.isArray(optionList) &&
      optionList.length > 0
    ) {
      setOptions(optionList);
    } else if (!inputValue) {
      setOptions([]);
    } else {
      getOptionsDelayed(inputValue, (filteredOptions) => {
        setOptions(filteredOptions);
      });
    }

    return () => {
      getOptionsDelayed.cancel();
    };
  }, [inputValue, getOptionsDelayed, optionList]);

  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value || null;

  const handleOnChange = (e, newVal, reason) => {
    if (reason !== 'blur') {
      onChange(newVal);
      if (handleChange) {
        handleChange(newVal, options);
      }

      if (!multiple && newVal) {
        setInputValue(handleOptionLabel(newVal));
      }
    }
  };

  const handleInputChange = (e, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
    } else if (reason === 'clear') {
      setInputValue('');
      onChange(multiple ? [] : null);
    } else if (reason === 'reset') {
      if (!multiple && value) {
        setInputValue(handleOptionLabel(value));
      }
    }
  };

  const handleBlur = (e) => {
    if (!multiple) {
      if (value) {
        setInputValue(handleOptionLabel(value));
      } else {
        setInputValue('');
      }
    } else {
      setInputValue('');
    }
    onBlur(e);
  };

  useEffect(() => {
    if (!multiple && value && !inputValue) {
      setInputValue(handleOptionLabel(value));
    }
  }, [value, multiple, inputValue, handleOptionLabel]);

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo={freeSolo}
      data-cy={cypressId}
      options={sortedOptions}
      getOptionLabel={handleOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(o) => o}
      defaultValue={checkInvalidInput(initial) ? undefined : initial}
      loading={loading || fetching}
      value={normalizedValue}
      inputValue={inputValue}
      onChange={handleOnChange}
      onInputChange={handleInputChange}
      noOptionsText={noOptionsText}
      filterSelectedOptions={multiple}
      disableCloseOnSelect={multiple}
      {...virtualizedProps}
      renderInput={(params) => (
        <TextField
          {...params}
          {...inputProps}
          {...TextFieldProps}
          error={containsError}
          helperText={getHelperText()}
          FormHelperTextProps={{
            style: {
              whiteSpace: 'pre-line',
            },
          }}
          InputProps={{
            ...(params.InputProps || {}),
            ...(InputProps || {}),
            endAdornment: (
              <>
                {(fetching || loading) && (
                  <InputAdornment position="end">
                    <Box mt={-1}>
                      <Loader size={20} thickness={6} />
                    </Box>
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            inputProps: {
              onFocus,
              ...params.inputProps,
              ...TextFieldProps.inputProps,
            },
          }}
          onBlur={handleBlur}
          InputLabelProps={{
            shrink:
              (!!TextFieldProps.placeholder && !noPlaceholderShrink) ||
              undefined,
            ...TextFieldProps.InputLabelProps,
          }}
        />
      )}
      {...restAutoCompleteProps}
    />
  );
};

export default FormAutoCompleteField;

FormAutoCompleteField.propTypes = {
  freeSolo: PropTypes.bool,
  fetching: PropTypes.bool,
  fetchOptions: PropTypes.func,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  handleChange: PropTypes.func,
  InputProps: PropTypes.shape(),
  input: PropTypes.shape(),
  limit: PropTypes.number,
  match: PropTypes.oneOf(['start', 'any']),
  meta: PropTypes.shape(),
  multiple: PropTypes.bool,
  noOptionsText: PropTypes.string,
  noPlaceholderShrink: PropTypes.bool,
  optionList: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
  ),
  parse: PropTypes.func,
  sort: PropTypes.bool,
  sortBy: PropTypes.func,
  TextFieldProps: PropTypes.shape(),
  virtualize: PropTypes.bool,
  'data-cy': PropTypes.string,
};

FormAutoCompleteField.defaultProps = {
  freeSolo: true,
  fetching: false,
  fetchOptions: null,
  getOptionLabel: (label) => label,
  isOptionEqualToValue: (option, value) => option === value,
  handleChange: () => null,
  input: {},
  limit: 500,
  match: 'start',
  meta: {},
  multiple: false,
  noOptionsText: 'No options',
  noPlaceholderShrink: false,
  optionList: [],
  parse: (v) => v,
  sort: false,
  sortBy: (optionList) => optionList.sort((a, b) => (a > b ? 1 : -1)),
  TextFieldProps: {},
  InputProps: {},
  virtualize: false,
  'data-cy': undefined,
};
